/**
 * Past-free / future-paid gating (web redesign 2026-07-09).
 *
 * Content describing dates strictly before today (user's local calendar)
 * is free forever. Today and later require premium — unless the chart is a
 * demo life (sales + proof demos stay fully unlocked).
 *
 * Single pure rule used by every gate. Premium state comes from
 * lib/subscription.ts (local tier today; Stripe later).
 */

import { getCurrentTier } from "./subscription";

/** Demo chart ids — fully unlocked including "future" windows. */
export const DEMO_CHART_IDS = new Set([
  "muhammad-ali",
  "mahatma-gandhi",
  "albert-einstein",
  "steve-jobs",
  "vesper", // Gandhi sample anchor used site-wide
]);

export interface DemoLife {
  id: string;
  name: string;
  /** ISO calendar date used to stop retrospective age-ledger data. */
  endDate: string;
}

/**
 * Historical demo lives are not "current" charts. Keeping their end dates
 * beside the demo allow-list prevents a posthumous API age of 0/current date
 * from leaking into the Watch UI.
 */
export const DEMO_LIVES: Record<string, DemoLife> = {
  "muhammad-ali": { id: "muhammad-ali", name: "Muhammad Ali", endDate: "2016-06-03" },
  "mahatma-gandhi": { id: "mahatma-gandhi", name: "Mahatma Gandhi", endDate: "1948-01-30" },
  vesper: { id: "vesper", name: "Mahatma Gandhi", endDate: "1948-01-30" },
  "albert-einstein": { id: "albert-einstein", name: "Albert Einstein", endDate: "1955-04-18" },
  "steve-jobs": { id: "steve-jobs", name: "Steve Jobs", endDate: "2011-10-05" },
  "indira-gandhi": { id: "indira-gandhi", name: "Indira Gandhi", endDate: "1984-10-31" },
};

export function getDemoLife(chartId: string | null | undefined): DemoLife | null {
  return chartId ? DEMO_LIVES[chartId] ?? null : null;
}

export function isDemoChart(chartId: string | null | undefined): boolean {
  return !!chartId && DEMO_CHART_IDS.has(chartId);
}

/**
 * The one gating rule. `contentDate` is the date a piece of content
 * describes (NOT the date it is viewed).
 */
export function isUnlocked(
  contentDate: Date,
  opts: { isPremium: boolean; isDemoChart?: boolean },
): boolean {
  if (opts.isPremium || opts.isDemoChart) return true;
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  return contentDate.getTime() < start.getTime();
}

/** Resolve premium from the local tier store. pro + scholar both unlock. */
export async function resolveIsPremium(): Promise<boolean> {
  const tier = await getCurrentTier();
  return tier === "pro" || tier === "scholar";
}

export function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

export function startOfYesterday(): Date {
  const d = startOfToday();
  d.setDate(d.getDate() - 1);
  return d;
}
